import { useQuery } from '@tanstack/react-query';
import { groupBy } from 'lodash-es';
import { useMemo, useState } from 'preact/hooks';

import Loader from '@/components/Loader';
import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { getUserProductsQueryOptions } from '@/queries/products';
import { GetUserProductsResponse, ParsedUserProduct } from '@/queries/products/types';

import { Content, Header, HeaderTitle, Section } from '../Section';
import UserProductDetailsModal from './UserProductDetailsModal';
import UserProductRow from './UserProductRow';

const DISPLAY_SOURCES = new Set(['stripe', 'bundling', 'giftup']);

const selectProducts = (data: GetUserProductsResponse) => {
  return data.list
    .map((item) => {
      const { product } = item;
      if (!product) return null;
      if (!product.source || !DISPLAY_SOURCES.has(product.source)) return null;
      if (product.source === 'stripe') {
        if (!product.price && product.productLookupKey !== 'lifetime-explorer') return null;
      }

      return {
        ...item,
        validUntil: Date.parse(item.validUntil),
        createdAt: Date.parse(item.createdAt),
        // Catalog `kind` groups recurring Stripe rows; UI branches on `productKind`.
        rowKey: product.source === 'stripe' && product.kind === 'recurring' ? 'recurring' : item.key,
      };
    })
    .filter((item): item is ParsedUserProduct => !!item);
};

const MyPurchaseSection = () => {
  const [now] = useLatestActiveTime();

  const { data, isLoading } = useQuery({
    ...getUserProductsQueryOptions(),
    select: selectProducts,
  });

  const { groupedProducts, allActiveUserProducts } = useMemo(() => {
    if (!data) {
      return { groupedProducts: {}, allActiveUserProducts: [] as ParsedUserProduct[] };
    }

    const allActiveUserProducts = data.filter((product) => product.validUntil > now);
    const groupedProducts = groupBy(
      Array.from(
        data
          .reduce((acc, item) => {
            if (!acc.has(item.rowKey)) {
              acc.set(item.rowKey, item);
            }
            return acc;
          }, new Map<string, ParsedUserProduct>())
          .values(),
      ),
      (item) => (item.validUntil > now ? 'active' : 'expired'),
    );

    return { groupedProducts, allActiveUserProducts };
  }, [data, now]);

  const { active = [], expired = [] } = groupedProducts;

  const [selectedUserProductId, setSelectedUserProductId] = useState<string | null>(null);
  const selectedUserProduct = useMemo(
    () => data?.find((product) => product._id === selectedUserProductId),
    [data, selectedUserProductId],
  );

  return (
    <Section>
      <Header>
        <div className="flex items-center justify-between">
          <HeaderTitle>My Purchases</HeaderTitle>
          <a href="/help?tab=plans" className="preflight preflight-a text-sm underline desktop:text-xl">
            <span className="mobile:sr-only">Plans & Passes </span>FAQ
          </a>
        </div>
      </Header>
      <Content>
        {isLoading ? (
          <div className="flex min-h-64 items-center justify-center">
            <Loader width="64px" height="64px" />
          </div>
        ) : !active.length && !expired.length ? (
          <div className="flex min-h-32 items-center justify-center text-stone-500">You have no purchases yet.</div>
        ) : (
          <div className="flex flex-col gap-7 desktop:gap-10">
            {active.length > 0 && (
              <div>
                <h4 className="preflight preflight-h4 mb-1 text-xl font-medium text-stone-500">Active</h4>
                <div className="preflight divide-y divide-stone-200">
                  {active.map((product) => (
                    <UserProductRow
                      key={product._id}
                      userProduct={product}
                      onSelect={() => setSelectedUserProductId(product._id)}
                    />
                  ))}
                </div>
              </div>
            )}
            {expired.length > 0 && (
              <div>
                <h4 className="preflight preflight-h4 mb-1 text-xl font-medium text-stone-500">Inactive</h4>
                <div className="preflight divide-y divide-stone-200">
                  {expired.map((product) => (
                    <UserProductRow
                      key={product._id}
                      userProduct={product}
                      onSelect={() => setSelectedUserProductId(product._id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {selectedUserProduct && (
          <UserProductDetailsModal
            userProduct={selectedUserProduct}
            activeUserProducts={allActiveUserProducts}
            onClose={() => setSelectedUserProductId(null)}
          />
        )}
      </Content>
    </Section>
  );
};

export default MyPurchaseSection;
